import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDynamicEntity } from './useDynamicEntity';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { hasuraClient } from '../utils/graphqlClient';
import { UPDATE_USER_REVENUE_ENTRY_STATUS_MUTATION } from '../graphql/mutations/userRevenueEntries';
import { revenueJobTypeNames, revenueProjectNoOrUnit } from '../utils/revenueJobInfo';

// Ciro kanali label sözlüğü — UI'da gösterim için.
const CIRO_KANALI_LABEL = {
  SATIS_CIROSU: 'Satış Cirosu',
  PROJE_YAZIM_CIROSU: 'Proje Yazım Cirosu',
  ONAY_SONRASI_CIROSU: 'Onay Sonrası Cirosu',
};

/**
 * Kullanıcı bazlı prim ciro girişleri hook'u.
 * - ROOT: tüm kayıtları görür/yönetir.
 * - Diğer roller: sadece kendi user_id'sine ait kayıtları görür/yönetir
 *   (Hasura permission'ları ile zaten kısıtlı, hook ayrıca where filter'ı uygular).
 *
 * Signature: object-accept. DynamicCrudPage hookConfig'i obje geçiriyor.
 *   useUserRevenueEntries({ userId, paginated })
 *
 * `userId` opsiyonel: profile sayfasında parent component user.id'yi
 * explicit geçer (Redux user load timing'i için defansif). Verilmezse
 * useAuth().user.id fallback kullanılır.
 */
const useUserRevenueEntries = (config = {}) => {
  const { userId = null, paginated = false } = config || {};
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const isRootUser = user?.role === 'ROOT';
  const targetUserId = userId || user?.id;

  // Permission-aware where condition.
  // - ROOT + explicit userId  → o user'a filtrele
  // - ROOT + no userId        → tüm şirketin kayıtları
  // - TEAM_MANAGER            → kendi ekibinin tüm üye kayıtları (read-only)
  // - Non-ROOT                → company + user_id (kendi kayıtları)
  const customWhereCondition = useMemo(() => {
    if (isRootUser) {
      if (userId) {
        const uid = parseInt(userId);
        return Number.isFinite(uid) ? { user_id: { _eq: uid } } : { user_id: { _eq: -1 } };
      }
      return {};
    }
    // TEAM_MANAGER: ekip üyelerinin tüm ciro kayıtlarını göre.
    // Filtre: user.team.team_manager_id === current user id.
    // useCiroAktar.js:51-55 emsali. Read-only akış CiroEklePage'te uygulanır.
    if (user?.role === 'TEAM_MANAGER' && user?.id) {
      const mgrId = parseInt(user.id);
      if (Number.isFinite(mgrId)) {
        return {
          company_id: { _eq: user?.companyId || -1 },
          user: { team: { team_manager_id: { _eq: mgrId } } },
        };
      }
    }
    // user?.id henüz yüklenmediğinde parseInt(undefined) → NaN → JSON null
    // gider ve Hasura "unexpected null value for type Int" döner. Sentinel
    // değerle (-1) hiçbir kayıt eşleşmez, sonraki fetch user yüklenince
    // doğru filtreyle yeniden tetiklenir.
    const uid = parseInt(targetUserId);
    return {
      company_id: { _eq: user?.companyId || -1 },
      user_id: { _eq: Number.isFinite(uid) ? uid : -1 },
    };
  }, [isRootUser, user?.role, user?.id, user?.companyId, userId, targetUserId]);

  const dynamicHook = useDynamicEntity({
    entityType: 'userRevenueEntries',
    gqlEntityKey: 'user_revenue_entries',
    queryName: 'GET_USER_REVENUE_ENTRIES_QUERY',
    createMutation: 'CREATE_USER_REVENUE_ENTRY_MUTATION',
    updateMutation: 'UPDATE_USER_REVENUE_ENTRY_MUTATION',
    deleteMutation: 'DELETE_USER_REVENUE_ENTRY_MUTATION',
    // Hasura policy: team_member/team_manager/... update_permissions.columns
    // listesinde user_id yok — kullanicilar kendi kayitlarinin sahipligini
    // degistirememeli. Update mode'da _set'ten cikar.
    updateStripFields: ['user_id'],
    serverPagination: paginated,
    aggregateKey: paginated ? 'user_revenue_entries_aggregate' : null,
    transformData: (row) => {
      const firmName =
        row.firm?.firm_short_name || row.firm?.firm_name || (row.firm_id ? `Firma #${row.firm_id}` : '-');

      const contractLabel = row.contract
        ? [row.contract?.contract_subject?.name, row.contract?.scope_of_work?.name]
            .filter(Boolean)
            .join(' / ') || `Sözleşme #${row.contract.id}`
        : null;

      const jobLabel = row.job
        ? row.job.job_title ||
          row.job.project_number ||
          row.job.job_type ||
          `İş #${row.job.id?.slice?.(0, 8)}`
        : null;

      const referenceLabel =
        row.ciro_kanali === 'SATIS_CIROSU'
          ? `${firmName} — ${contractLabel || '-'}`
          : `${firmName} — ${jobLabel || '-'}`;

      // Is turu ismi: 3 kaynaktan fallback zinciri.
      //   1. jobs.job_type (legacy text enum, mesela 'TUBITAK')
      //   2. jobs.job_type_rel.name (yeni dinamik system — text NULL iken
      //      bu isim dolu gelir)
      //   3. bulunamadi → null
      // Legacy text varsa i18n key deneriz, defaultValue ile fallback.
      // Yeni dinamik'te text NULL, ham isim direkt kullanilir. Onceden
      // sadece jobs.job_type text alanina bakiliyordu ve dinamik gecisten
      // sonra bazi satirlarda "Is Turu" kolonu bos kaliyordu.
      // Satis cirosunda row.job yok → sozlesmenin islerinden (revenueJobInfo).
      const jobTypeLabel = revenueJobTypeNames(row)
        .map((ad) => t(`jobs.jobTypes.${ad}`, { defaultValue: ad }))
        .join(', ') || null;
      // Proje no: her kanalda — satis cirosunda sozlesmenin islerinden
      // (bkz. utils/revenueProjectNumber).
      const projectNumber = revenueProjectNoOrUnit(row);

      return {
        ...row,
        user_name: row.user
          ? `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim()
          : `Kullanıcı #${row.user_id}`,
        company_name: row.company?.name || `Company ${row.company_id}`,
        firm_name: firmName,
        // Modallarda (onay / detay) kisa ad degil TAM unvan gosterilir.
        firm_full_name: row.firm?.firm_name || row.contract?.firm?.firm_name || firmName,
        project_number: projectNumber,
        job_type_label: jobTypeLabel,
        contract_label: contractLabel,
        job_label: jobLabel,
        // CiroTalepleriPage ekip filtresi icin top-level alan. Eski sayfalar
        // bunu kullanmiyor; sadece okuma — breaking degil.
        team_id: row.user?.team_id ?? null,
        ciro_kanali_label: CIRO_KANALI_LABEL[row.ciro_kanali] || row.ciro_kanali,
        period_label: `${row.yil}/Q${row.ceyrek}`,
        // Table'da gösterilen birleşik referans (firma — sözleşme/iş).
        referans:
          row.ciro_kanali === 'SATIS_CIROSU'
            ? (contractLabel || '-')
            : (jobLabel || '-'),
        reference_label: referenceLabel,
        userId: row.user_id,
        companyId: row.company_id,
      };
    },
    transformFormData: (formData) => {
      const kanal = formData.ciro_kanali;
      const isSatis = kanal === 'SATIS_CIROSU';

      // user_id: profile sahibi. NaN olursa Hasura "Int için null" diye
      // anlamsız bir hata atar — burada açık mesaj ver.
      const uidRaw = formData.user_id ?? targetUserId ?? user?.id;
      const uid = parseInt(uidRaw);
      if (!Number.isFinite(uid)) {
        throw new Error('Kullanıcı bilgisi yüklenmemiş, sayfayı yenileyip tekrar deneyin');
      }
      const yilNum = parseInt(formData.yil);
      const ceyrekNum = parseInt(formData.ceyrek);
      if (!Number.isFinite(yilNum) || !Number.isFinite(ceyrekNum)) {
        throw new Error('Yıl ve çeyrek seçimi gereklidir');
      }
      const firmIdNum = formData.firm_id ? parseInt(formData.firm_id) : null;
      if (!Number.isFinite(firmIdNum)) {
        throw new Error('Firma seçimi gereklidir');
      }

      const bonusRevenueTypeIdNum = formData.bonus_revenue_type_id
        ? parseInt(formData.bonus_revenue_type_id, 10)
        : null;

      // ay_sayisi: CiroEklePage submit'inde "Tutar Getir" hesaplamasından
      // gelen quarterMonths. Eski formlar göndermezse null kalır (eski entry
      // davranışına geri düşer — pastMonths tutardan türetilir).
      const aySayisiRaw = formData.ay_sayisi;
      const aySayisiNum = aySayisiRaw == null || aySayisiRaw === ''
        ? null
        : parseInt(aySayisiRaw, 10);

      // secili_aylar: ONAY_SONRASI icin kullanicinin sectigi aylar (INT[]).
      // 1..12 disi degerler + duplicate filtrelenir; bos array veya
      // undefined ise null gonderilir (DB nullable).
      const seciliAylarSanitized = Array.isArray(formData.secili_aylar)
        ? [...new Set(formData.secili_aylar.map((n) => parseInt(n, 10)).filter((n) => Number.isInteger(n) && n >= 1 && n <= 12))]
        : null;

      const base = {
        user_id: uid,
        yil: yilNum,
        ceyrek: ceyrekNum,
        ciro_kanali: kanal,
        bonus_revenue_type_id: Number.isInteger(bonusRevenueTypeIdNum) ? bonusRevenueTypeIdNum : null,
        firm_id: firmIdNum,
        tutar: parseFloat(formData.tutar) || 0,
        ay_sayisi: Number.isFinite(aySayisiNum) ? aySayisiNum : null,
        secili_aylar: (seciliAylarSanitized && seciliAylarSanitized.length > 0) ? seciliAylarSanitized : null,
        // Default: true (manuel). CiroEklePage "Otomatik Doldur" butonuna
        // basildiginda formData.is_manual_amount=false set edip yollar;
        // sonrasinda tutar/ay/ceyrek elle degisirse tekrar true yapar.
        is_manual_amount: formData.is_manual_amount === false ? false : true,
        // toplam_ay (y payda override) — sadece ROOT set edebilir. NULL ise
        // sistem computeQuarterAmount.y kullanir. transformFormData default'ta
        // null gonderir; form field'i sadece ROOT'a gorunur (showIf).
        toplam_ay: (formData.toplam_ay != null && formData.toplam_ay !== '' && Number.isFinite(Number(formData.toplam_ay)))
          ? Number(formData.toplam_ay)
          : null,
        aciklama: formData.aciklama || null,
      };

      // Channel CHECK constraint: SATIS → contract zorunlu, YAZIM/ONAY → job zorunlu.
      if (isSatis) {
        const cId = formData.contract_id ? parseInt(formData.contract_id) : NaN;
        if (!Number.isFinite(cId)) {
          throw new Error('Satış cirosu için sözleşme seçimi gereklidir');
        }
        base.contract_id = cId;
        base.job_id = null;
      } else {
        if (!formData.job_id) {
          throw new Error('Yazım/Onay cirosu için iş seçimi gereklidir');
        }
        base.job_id = formData.job_id;
        base.contract_id = null;
      }

      return base;
    },
    customWhereCondition,
  });

  // Status mutation handler — APPROVED veya REJECTED'a tasiyan tek noktali
  // akis. Sayfa-seviyesi handler'larin yerini alir; notification + fetchData
  // refresh burada yapilir. id parametresi number veya { id } satir objesi
  // alabilir (WorkflowPage.onApprove(id), CiroTalepDetailModal.onApprove(row)
  // her iki cagri ornegini de destekler).
  const handleStatusChange = useCallback(
    async (idOrRow, newStatus, rejectReason = null) => {
      const id =
        idOrRow && typeof idOrRow === 'object' ? idOrRow.id : idOrRow;
      if (id == null) return;
      try {
        await hasuraClient.mutate(UPDATE_USER_REVENUE_ENTRY_STATUS_MUTATION, {
          id,
          status: newStatus,
          // APPROVED'da reject_reason'i null'la — eski reddedilmis kayit
          // yeniden onaylanirsa eski neden temizlensin.
          rejectReason: newStatus === 'REJECTED' ? rejectReason || null : null,
        });
        await dynamicHook.fetchData();
        const successKey =
          newStatus === 'APPROVED'
            ? 'userRevenueEntries.actions.approveSuccess'
            : 'userRevenueEntries.actions.rejectSuccess';
        showNotification(t(successKey), 'success');
      } catch (error) {
        const errorKey =
          newStatus === 'APPROVED'
            ? 'userRevenueEntries.actions.approveError'
            : 'userRevenueEntries.actions.rejectError';
        showNotification(t(errorKey), 'error');
        throw error;
      }
    },
    [dynamicHook, showNotification, t]
  );

  const handleApprove = useCallback(
    (idOrRow) => handleStatusChange(idOrRow, 'APPROVED'),
    [handleStatusChange]
  );

  // WorkflowPage'in dahili RejectReasonModal'i (id, reason) ile cagirir.
  const handleReject = useCallback(
    (idOrRow, reason) => handleStatusChange(idOrRow, 'REJECTED', reason),
    [handleStatusChange]
  );

  return {
    ...dynamicHook,
    isRootUser,
    handleApprove,
    handleReject,
    handleStatusChange,
  };
};

export { useUserRevenueEntries };
